/**
 * 발표 모드 슬라이드 조립.
 * 채팅(캔버스 HTML / 쪽 이미지)과 라이브러리(저장된 쪽 이미지)가 같이 쓴다.
 */

import { freeDesignRef } from '../../utils/freeDesignOutline';
import { parseOutline } from '../../utils/libraryFamily';

const hasUrl = (url) => typeof url === 'string' && url.trim().length > 0;

export const cleanImageUrls = (urls) => {
  if (!Array.isArray(urls)) return [];
  return urls.filter(hasUrl).map((url) => url.trim());
};

export const clampSlideIndex = (index, total) => {
  if (!total) return 0;
  const next = Number(index);
  if (!Number.isInteger(next) || next < 0) return 0;
  return Math.min(next, total - 1);
};

export const createPresentationSession = (slides, initialIndex = 0) => {
  if (!Array.isArray(slides) || slides.length === 0) return null;
  return { initialIndex: clampSlideIndex(initialIndex, slides.length), slides };
};

export const buildCanvasPresentationSlides = (slides, themeId = null) => {
  if (!Array.isArray(slides) || slides.length === 0) return [];
  return slides.map((slide, index) => ({
    index,
    themeId,
    type: 'canvas',
    slide,
  }));
};

export const buildImagePresentationSlides = (hiresUrls, previewUrls) => {
  const previews = Array.isArray(previewUrls) ? previewUrls : [];
  const hires = Array.isArray(hiresUrls) ? hiresUrls : previews;
  const length = Math.max(previews.length, hires.length);
  const slides = [];
  for (let i = 0; i < length; i += 1) {
    const preview = hasUrl(previews[i]) ? previews[i].trim() : null;
    const src = hasUrl(hires[i]) ? hires[i].trim() : preview;
    if (!src) continue;
    slides.push({
      index: slides.length,
      fullUrl: src,
      previewSrc: preview || src,
      src,
      thumbUrl: preview || src,
      type: 'image',
    });
  }
  return slides;
};

export const collectLibraryPptImageUrls = (item) => {
  if (!item || typeof item !== 'object') return [];
  const candidates = [
    item.versions?.image?.page_urls,
    item.preview_urls,
    item.previewUrls,
    item.artifact?.previewUrls,
    item.source_data?.previews,
    item.source_data?.previewUrls,
  ];
  let best = [];
  candidates.forEach((list) => {
    const clean = cleanImageUrls(list);
    if (clean.length > best.length) best = clean;
  });
  if (best.length === 0 && hasUrl(item.thumbnail_url)) {
    return [item.thumbnail_url.trim()];
  }
  return best;
};

export const inferLibraryPptPageCount = (item) => {
  const recorded = Number(item?.page_count ?? item?.pageCount ?? item?.artifact?.pageCount);
  if (Number.isInteger(recorded) && recorded > 0) return recorded;
  const slides = item?.outline?.slides;
  if (Array.isArray(slides) && slides.length > 0) return slides.length;
  return collectLibraryPptImageUrls(item).length;
};

const sourceModeOf = (item) => {
  const raw = item?.latest_version
    || item?.artifact?.sourceMode
    || item?.sourceMode
    || item?.source_data?.sourceMode
    || item?.download_mode
    || item?.pptDownloadMode;
  if (typeof raw === 'string' && raw.trim()) return raw.trim().toLowerCase();
  if (item?.versions?.image && !item?.versions?.text) return 'image';
  if (item?.versions?.text && !item?.versions?.image) return 'text';
  return '';
};

export const isLikelyImagePpt = (item) => {
  const mode = sourceModeOf(item);
  if (mode === 'image') return true;
  if (mode === 'text' || mode === 'hybrid') return false;
  const urls = collectLibraryPptImageUrls(item);
  const pages = inferLibraryPptPageCount(item);
  return urls.length >= 2 && (pages <= 1 || urls.length >= 2);
};

export const collectLibraryPptOutline = (item, hydratedOutline) => {
  const outline = hydratedOutline
    || item?.versions?.text?.outline
    || item?.outline
    || item?.source_data?.outline
    || null;
  const slides = Array.isArray(outline?.slides) ? outline.slides : [];
  return {
    slides,
    themeId: outline?.canvas_theme || item?.outline?.canvas_theme || null,
  };
};

/** 텍스트형 덱(자유 디자인)이면 { deckId, previewUrls }. 옛 텍스트형(캔버스 개요)이면 null. */
export const libraryFreeDesignDeck = (item) => {
  const candidates = [item?.versions?.text?.outline, item?.outline, item?.source_data?.outline];
  for (const raw of candidates) {
    const ref = freeDesignRef(parseOutline(raw));
    if (ref) return { deckId: ref.deck_id, previewUrls: cleanImageUrls(ref.preview_urls) };
  }
  return null;
};

export const listLibraryPresentVersions = (item, hydratedOutline) => {
  if (!item || item.content_type !== 'ppt') return [];
  const versions = [];
  const outline = collectLibraryPptOutline(item, hydratedOutline);
  const deck = libraryFreeDesignDeck(item);
  if (deck?.previewUrls.length) {
    // 텍스트형 덱은 개요(옛 캔버스)로 그리면 실제 덱과 다르다. 덱 장 그림으로 발표하고, 열 수 있으면 덱을 직접 연다.
    versions.push({
      deckId: deck.deckId,
      description: '글자 선명·편집 반영',
      id: 'text',
      label: '텍스트형',
      slides: buildImagePresentationSlides(deck.previewUrls, deck.previewUrls),
    });
  } else if (outline.slides.length > 0) {
    versions.push({
      description: '글자 선명·편집 반영',
      id: 'text',
      label: '텍스트형',
      slides: buildCanvasPresentationSlides(outline.slides, outline.themeId),
    });
  }
  const urls = collectLibraryPptImageUrls(item);
  const imageOk = Boolean(item.versions?.image)
    || (isLikelyImagePpt(item) && urls.length > 0)
    || urls.length >= 2;
  if (imageOk && urls.length > 0) {
    versions.push({
      description: '디자인 그대로',
      id: 'image',
      label: '이미지형',
      slides: buildImagePresentationSlides(urls, urls),
    });
  }
  return versions;
};

/**
 * 라이브러리 발표 가능 여부.
 * 이미지형: 쪽 이미지 1장 이상.
 * 텍스트형: 상세 모달과 같은 outline.slides(HtmlSlideRenderer)로 발표.
 * 둘 다 있으면 versions.length === 2 이고 slides는 비운다(버전 시트에서 고른다).
 * 표지 썸네일 1장만 있고 outline도 없으면 숨김.
 */
export const canPresentLibraryPpt = (item, hydratedOutline, options = {}) => {
  if (!item || item.content_type !== 'ppt') {
    return { ok: false, reason: 'not_ppt', slides: [], versions: [] };
  }
  const versions = listLibraryPresentVersions(item, hydratedOutline);
  const preferred = options.mode === 'text' || options.mode === 'image' ? options.mode : null;
  if (preferred) {
    const chosen = versions.find((version) => version.id === preferred);
    if (chosen) return { ok: true, reason: null, slides: chosen.slides, deckId: chosen.deckId || null, versions };
    return { ok: false, reason: 'no_slides', slides: [], versions };
  }
  if (versions.length === 1) {
    return { ok: true, reason: null, slides: versions[0].slides, deckId: versions[0].deckId || null, versions };
  }
  if (versions.length > 1) {
    return { ok: true, reason: null, slides: [], versions };
  }
  const urls = collectLibraryPptImageUrls(item);
  if (urls.length === 1) {
    return { ok: false, reason: 'single_thumbnail', slides: [], versions: [] };
  }
  return { ok: false, reason: 'no_slides', slides: [], versions: [] };
};
